import React, { useState, useEffect } from 'react';
import API from '../../api';
import { toast } from 'react-toastify';

export default function AdminSubscriptionPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    duration: 3,  // Keep as number
    price: '',
    displayPrice: '',
    discount: 0,
    features: '',
    description: '',
    isActive: true,
    isPopular: false,
    sortOrder: 0
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await API.get('/subscriptions/plans?includeInactive=true');
      setPlans(response.data.plans || []);
    } catch (error) {
      toast.error('Failed to fetch plans');
    }
    setLoading(false);
  };

  // ✅ FIXED: Convert numeric fields to numbers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let newValue;
    
    if (type === 'checkbox') {
      newValue = checked;
    } else if (['duration', 'price', 'displayPrice', 'discount', 'sortOrder'].includes(name)) {
      // Convert numeric fields to numbers
      newValue = value === '' ? '' : Number(value);
    } else {
      newValue = value;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const openCreateModal = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      duration: 3,
      price: '',
      displayPrice: '',
      discount: 0,
      features: '',
      description: '',
      isActive: true,
      isPopular: false,
      sortOrder: 0
    });
    setShowModal(true);
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      duration: Number(plan.duration),  // ✅ Ensure it's a number
      price: plan.price / 100,
      displayPrice: plan.displayPrice ? plan.displayPrice / 100 : '',
      discount: plan.discount || 0,
      features: plan.features?.join('\n') || '',
      description: plan.description || '',
      isActive: plan.isActive,
      isPopular: plan.isPopular || false,
      sortOrder: plan.sortOrder || 0
    });
    setShowModal(true);
  };

  // ✅ FIXED: Properly convert all fields
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Convert duration to number and validate
    const duration = Number(formData.duration);
    
    
    
    // Validate duration before sending
    if (![3, 6, 12].includes(duration)) {
      toast.error(`Invalid duration: ${duration}. Must be 3, 6, or 12 months.`);
      setSubmitting(false);
      return;
    }
    
    const payload = {
      name: formData.name.trim(),
      duration: duration,  // ✅ Now it's definitely a number
      price: Math.round(parseFloat(formData.price) * 100),  // Convert to paisa
      displayPrice: formData.displayPrice ? Math.round(parseFloat(formData.displayPrice) * 100) : null,
      discount: parseInt(formData.discount) || 0,
      features: formData.features.split('\n').map(f => f.trim()).filter(f => f),
      description: formData.description?.trim() || '',
      isActive: Boolean(formData.isActive),
      isPopular: Boolean(formData.isPopular),
      sortOrder: parseInt(formData.sortOrder) || 0
    };

    

    try {
      if (editingPlan) {
        await API.put(`/subscriptions/plans/${editingPlan._id}`, payload);
        toast.success('Plan updated successfully');
      } else {
        await API.post('/subscriptions/plans', payload);
        toast.success('Plan created successfully');
      }
      setShowModal(false);
      fetchPlans();
    } catch (error) {
      console.error('API Error:', error.response?.data);
      toast.error(error.response?.data?.message || 'Failed to save plan');
    }
    setSubmitting(false);
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    
    try {
      await API.delete(`/subscriptions/plans/${planId}`);
      toast.success('Plan deleted successfully');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete plan');
    }
  };

  const toggleActive = async (plan) => {
    try {
      await API.put(`/subscriptions/plans/${plan._id}`, {
        isActive: !plan.isActive
      });
      toast.success(`Plan ${plan.isActive ? 'deactivated' : 'activated'}`);
      fetchPlans();
    } catch (error) {
      toast.error('Failed to update plan status');
    }
  };

  const formatPrice = (priceInPaisa) => {
    return `Rs. ${(priceInPaisa / 100).toLocaleString()}`;
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <p>Loading plans...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>💎 Subscription Plans</h1>
          <p style={styles.subtitle}>Manage your subscription packages</p>
        </div>
        <button onClick={openCreateModal} style={styles.addButton}>
          + Add New Plan
        </button>
      </div>

      {/* Plans Grid */}
      <div style={styles.plansGrid}>
        {plans.map((plan) => (
          <div 
            key={plan._id} 
            style={{
              ...styles.planCard,
              opacity: plan.isActive ? 1 : 0.6,
              borderColor: plan.isPopular ? '#667eea' : '#e2e8f0'
            }}
          >
            {plan.isPopular && (
              <div style={styles.popularTag}>Popular</div>
            )}
            
            {!plan.isActive && (
              <div style={styles.inactiveTag}>Inactive</div>
            )}

            <h2 style={styles.planName}>{plan.name}</h2>
            <p style={styles.planDuration}>{plan.duration} Months</p>
            
            <div style={styles.priceSection}>
              <span style={styles.price}>{formatPrice(plan.finalPrice || plan.price)}</span>
              {plan.discount > 0 && (
                <span style={styles.discountBadge}>{plan.discount}% OFF</span>
              )}
            </div>

            {plan.features && plan.features.length > 0 && (
              <ul style={styles.featuresList}>
                {plan.features.slice(0, 3).map((feature, idx) => (
                  <li key={idx} style={styles.featureItem}>✓ {feature}</li>
                ))}
                {plan.features.length > 3 && (
                  <li style={styles.moreFeatures}>+{plan.features.length - 3} more</li>
                )}
              </ul>
            )}

            <div style={styles.cardActions}>
              <button 
                onClick={() => openEditModal(plan)}
                style={styles.editBtn}
              >
                ✏️ Edit
              </button>
              <button 
                onClick={() => toggleActive(plan)}
                style={{
                  ...styles.toggleBtn,
                  backgroundColor: plan.isActive ? '#fee2e2' : '#dcfce7',
                  color: plan.isActive ? '#991b1b' : '#166534'
                }}
              >
                {plan.isActive ? '🚫 Deactivate' : '✅ Activate'}
              </button>
              <button 
                onClick={() => handleDelete(plan._id)}
                style={styles.deleteBtn}
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingPlan ? '✏️ Edit Plan' : '➕ Create New Plan'}
              </h2>
              <button onClick={() => setShowModal(false)} style={styles.closeBtn}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Plan Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Basic Plan"
                    required
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Duration (Months) *</label>
                  <select
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    required
                    style={styles.input}
                  >
                    <option value={3}>3 Months</option>
                    <option value={6}>6 Months</option>
                    <option value={12}>12 Months</option>
                  </select>
                  {/* Debug display - remove in production */}
                  <small style={{ color: '#94a3b8', fontSize: '11px' }}>
                    Value: {formData.duration} | Type: {typeof formData.duration}
                  </small>
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Price (Rs.) *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="1000"
                    required
                    min="1"
                    step="0.01"
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Original Price (Rs.) - for strikethrough</label>
                  <input
                    type="number"
                    name="displayPrice"
                    value={formData.displayPrice}
                    onChange={handleInputChange}
                    placeholder="1500"
                    min="0"
                    step="0.01"
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Discount (%)</label>
                  <input
                    type="number"
                    name="discount"
                    value={formData.discount}
                    onChange={handleInputChange}
                    placeholder="10"
                    min="0"
                    max="100"
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Sort Order</label>
                  <input
                    type="number"
                    name="sortOrder"
                    value={formData.sortOrder}
                    onChange={handleInputChange}
                    placeholder="0"
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Features (one per line)</label>
                <textarea
                  name="features"
                  value={formData.features}
                  onChange={handleInputChange}
                  placeholder="Unlimited access&#10;Priority support&#10;Advanced analytics"
                  rows={4}
                  style={styles.textarea}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of the plan..."
                  rows={2}
                  style={styles.textarea}
                />
              </div>

              <div style={styles.checkboxRow}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="isPopular"
                    checked={formData.isPopular}
                    onChange={handleInputChange}
                  />
                  Mark as Popular
                </label>
              </div>

              <div style={styles.modalActions}>
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)}
                  style={styles.cancelBtn}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  style={{
                    ...styles.submitBtn,
                    opacity: submitting ? 0.7 : 1,
                    cursor: submitting ? 'not-allowed' : 'pointer'
                  }}
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : (editingPlan ? 'Update Plan' : 'Create Plan')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '30px',
  },
  loadingContainer: {
    padding: '50px',
    textAlign: 'center',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    margin: 0,
  },
  subtitle: {
    color: '#64748b',
    marginTop: '5px',
  },
  addButton: {
    padding: '12px 24px',
    backgroundColor: '#667eea',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  plansGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '24px',
  },
  planCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '2px solid',
    position: 'relative',
  },
  popularTag: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    backgroundColor: '#667eea',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  inactiveTag: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  planName: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '4px',
  },
  planDuration: {
    color: '#64748b',
    fontSize: '14px',
    marginBottom: '15px',
  },
  priceSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  price: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
  },
  discountBadge: {
    backgroundColor: '#dcfce7',
    color: '#166534',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
  },
  featuresList: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 20px 0',
  },
  featureItem: {
    padding: '6px 0',
    color: '#4a5568',
    fontSize: '14px',
  },
  moreFeatures: {
    color: '#94a3b8',
    fontSize: '13px',
    fontStyle: 'italic',
  },
  cardActions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  editBtn: {
    flex: 1,
    padding: '10px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  toggleBtn: {
    flex: 1,
    padding: '10px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  deleteBtn: {
    padding: '10px 14px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  
  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #f1f5f9',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#94a3b8',
  },
  form: {
    padding: '24px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  checkboxRow: {
    display: 'flex',
    gap: '24px',
    marginBottom: '24px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  cancelBtn: {
    padding: '12px 24px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '12px 24px',
    backgroundColor: '#667eea',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};